import Uploader from '@/components/PostEditor/Uploader'
import ProfileBanner from '@/components/ProfileBanner'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { invalidateMetadata } from '@/atoms/invalidation'
import { now } from '@/lib/utils'
import * as kinds from '@nostr/tools/kinds'
import { addUsernameToIndex, publish, publishOpaquely } from '@/services/client.service'
import { generateImageByPubkey } from '@/lib/pubkey'
import { isEmail } from '@/lib/utils'
import { accountAtom } from '@/atoms/session'
import { useCurrentProfile } from '@/atoms/profile'
import {
  markProfileEdited,
  openSaveNsecModal,
  shouldPromptAfterProfileEdit
} from '@/atoms/key-backup'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading03Icon, Upload01Icon } from '@hugeicons/core-free-icons'
import { forwardRef, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { toast } from 'sonner'
import { loadNostrUser } from '@nostr/gadgets/metadata'
import { loadPaymentTargets } from '@/components/PaymentTargets'
import { getErrorMessage } from '@/lib/error'

const placeholders = [
  'monero',
  'bitcoin',
  'cashme',
  'bip352',
  'paypal',
  'venmo',
  'ethereum',
  'bitcoincash',
  'solana'
]

const ProfileEditorPage = forwardRef(({ index }: { index?: number }, ref) => {
  const { t } = useTranslation()
  const account = useAtomValue(accountAtom)
  const profile = useCurrentProfile()
  const [banner, setBanner] = useState<string>('')

  useEffect(() => {
    if (!profile) return
    document.title = t('Profile Edit | {{appName}}', {
      appName: window.hallway.universe.appName
    })
    history.replaceState(null, '')
  }, [profile])

  useEffect(() => {
    if (!window.hallway.universe.enableNipA3 || !profile) return

    setPaymentTargetsChanged(false)
    let cancelled = false
    loadPaymentTargets(profile.pubkey)
      .then(({ items }) => {
        if (cancelled) return
        setPaymentRows([
          ...items.map((item) => ({ type: item.type, address: item.address })),
          { type: '', address: '' }
        ])
      })
      .catch((error) =>
        toast.error(`${t('Failed to load payment targets')}: ${getErrorMessage(error)}`)
      )
    return () => {
      cancelled = true
    }
  }, [profile?.pubkey])

  const [avatar, setAvatar] = useState<string>('')
  const [name, setName] = useState<string>('')
  const [about, setAbout] = useState<string>('')
  const [website, setWebsite] = useState<string>('')
  const [nip05, setNip05] = useState<string>('')
  const [nip05Error, setNip05Error] = useState<string>('')
  const [paymentRows, setPaymentRows] = useState<{ type: string; address: string }[]>([
    { type: '', address: '' }
  ])
  const [metadataChanged, setMetadataChanged] = useState(false)
  const [paymentTargetsChanged, setPaymentTargetsChanged] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploadingBanner, setUploadingBanner] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)

  const defaultImage = useMemo(
    () => (account ? generateImageByPubkey(account.pubkey) : undefined),
    [account]
  )

  useEffect(() => {
    setMetadataChanged(false)
    if (profile) {
      setBanner(profile.metadata.banner ?? '')
      setAvatar(profile.metadata.picture ?? '')
      setName(profile.metadata.name ?? '')
      setAbout(profile.metadata.about ?? '')
      setWebsite(profile.metadata.website ?? '')
      setNip05(profile.metadata.nip05 ?? '')
    } else {
      setBanner('')
      setAvatar('')
      setName('')
      setAbout('')
      setWebsite('')
      setNip05('')
    }
  }, [profile])

  if (!account || !profile) return null

  const controls = (
    <div className="pr-3">
      <Button
        className="w-16 rounded-full"
        onClick={save}
        disabled={saving || (!metadataChanged && !paymentTargetsChanged)}
      >
        {saving ? <HugeiconsIcon icon={Loading03Icon} className="animate-spin" /> : t('Save')}
      </Button>
    </div>
  )

  return (
    <SecondaryPageLayout
      ref={ref}
      index={index}
      title={profile?.shortName ?? ''}
      controls={controls}
    >
      <div className="relative bg-cover bg-center mb-2">
        <Uploader
          onUploadSuccess={onBannerUploadSuccess}
          onUploadStart={() => setUploadingBanner(true)}
          onUploadEnd={() => setUploadingBanner(false)}
          className="w-full relative cursor-pointer"
        >
          <ProfileBanner banner={banner} pubkey={account.pubkey} className="w-full aspect-[3/1]" />
          <div className="absolute top-0 bg-muted/30 w-full h-full flex flex-col justify-center items-center">
            {uploadingBanner ? (
              <HugeiconsIcon icon={Loading03Icon} size={36} className="animate-spin" />
            ) : (
              <HugeiconsIcon icon={Upload01Icon} size={36} />
            )}
          </div>
        </Uploader>
        <Uploader
          onUploadSuccess={onAvatarUploadSuccess}
          onUploadStart={() => setUploadingAvatar(true)}
          onUploadEnd={() => setUploadingAvatar(false)}
          className="w-24 h-24 absolute bottom-0 left-4 translate-y-1/2 border-4 border-background cursor-pointer rounded-full"
        >
          <Avatar className="w-full h-full">
            <AvatarImage src={avatar} className="object-cover object-center" />
            <AvatarFallback>
              <img src={defaultImage} />
            </AvatarFallback>
          </Avatar>
          <div className="absolute top-0 bg-muted/30 w-full h-full rounded-full flex flex-col justify-center items-center">
            {uploadingAvatar ? (
              <HugeiconsIcon icon={Loading03Icon} className="animate-spin" />
            ) : (
              <HugeiconsIcon icon={Upload01Icon} />
            )}
          </div>
        </Uploader>
      </div>
      <div className="pt-14 px-4 flex flex-col gap-4">
        <Item>
          <Label htmlFor="profile-username-input">{t('Name')}</Label>
          <Input
            id="profile-username-input"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setMetadataChanged(true)
            }}
          />
        </Item>
        <Item>
          <Label htmlFor="profile-about-textarea">{t('About')}</Label>
          <Textarea
            id="profile-about-textarea"
            className="h-44"
            value={about}
            onChange={(e) => {
              setAbout(e.target.value)
              setMetadataChanged(true)
            }}
          />
        </Item>
        <Item>
          <Label htmlFor="profile-nip05-input">{t('Nostr Address (NIP-05)')}</Label>
          <Input
            id="profile-nip05-input"
            value={nip05}
            onChange={(e) => {
              setNip05Error('')
              setNip05(e.target.value)
              setMetadataChanged(true)
            }}
            className={nip05Error ? 'border-destructive' : ''}
          />
          {nip05Error && <div className="text-xs text-destructive pl-3">{nip05Error}</div>}
        </Item>
        {window.hallway.universe.enableNipA3 && (
          <Item>
            <Label htmlFor="profile-payment-targets-input">{t('Payment Targets')}</Label>
            {paymentRows.map((row, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  className="w-32 shrink-0"
                  placeholder={placeholders[index % placeholders.length]}
                  value={row.type}
                  onChange={(e) => {
                    updatePaymentRow(index, { type: e.target.value })
                    setPaymentTargetsChanged(true)
                  }}
                />
                <Input
                  id="profile-payment-targets-input"
                  value={row.address}
                  onChange={(e) => {
                    updatePaymentRow(index, { address: e.target.value })
                    setPaymentTargetsChanged(true)
                  }}
                />
              </div>
            ))}
          </Item>
        )}
      </div>
    </SecondaryPageLayout>
  )

  function updatePaymentRow(index: number, update: Partial<{ type: string; address: string }>) {
    setPaymentRows((prev) => {
      const next = prev
        .map((row, i) => (i === index ? { ...row, ...update } : row))
        .filter(
          (row, i) =>
            (i === index ? row.address : row.address.trim()) !== '' ||
            (i === index ? row.type : row.type.trim()) !== ''
        )

      next.push({ type: '', address: '' })
      return next
    })
  }

  async function save() {
    if (nip05 && !isEmail(nip05)) {
      setNip05Error(t('Invalid NIP-05 address'))
      return
    }

    setSaving(true)
    setMetadataChanged(false)
    setPaymentTargetsChanged(false)
    let shouldInvalidateMetadata = false

    const newProfileEvent = metadataChanged
      ? await publish({
          kind: kinds.Metadata,
          content: JSON.stringify({
            ...profile!.metadata,
            display_name: profile!.metadata.display_name || undefined,
            name,
            about,
            website,
            nip05,
            banner,
            picture: avatar
          }),
          tags: [],
          created_at: now()
        })
      : undefined
    if (metadataChanged && !newProfileEvent) {
      console.warn('failed to publish updated profile event')
      toast.error(t('Failed to publish updated profile event.'))
      setSaving(false)
      return
    }

    if (paymentTargetsChanged && window.hallway.universe.enableNipA3) {
      const paymentTags = paymentRows
        .map((row) => [row.type.trim().toLowerCase(), row.address.trim()] as const)
        .filter(([type, address]) => type && address)
        .map(([type, address]) => ['payto', type, address])
      const newPaymentTargetsEvent = await publishOpaquely({
        kind: 10133,
        content: '',
        tags: paymentTags,
        created_at: now()
      }).catch((err) => {
        console.warn('failed to publish updated payment targets event:', getErrorMessage(err))
        return null
      })
      if (newPaymentTargetsEvent) {
        loadPaymentTargets(account!.pubkey, undefined, newPaymentTargetsEvent)
        shouldInvalidateMetadata = true
      } else {
        toast.error(t('Failed to publish payment targets.'))
      }
    }

    const promptForBackup =
      !!newProfileEvent && markProfileEdited() && shouldPromptAfterProfileEdit()

    if (newProfileEvent) {
      const refreshed = await loadNostrUser({
        pubkey: profile!.pubkey,
        refreshStyle: newProfileEvent
      })
      if (refreshed) addUsernameToIndex(refreshed)
      shouldInvalidateMetadata = true
    }
    if (shouldInvalidateMetadata) invalidateMetadata()

    setSaving(false)
    window.history.back()
    if (promptForBackup) {
      // let PageManager handle navigation before registering this modal
      setTimeout(openSaveNsecModal, 0)
    }
  }

  function onBannerUploadSuccess({ url }: { url: string }) {
    setBanner(url)
    setMetadataChanged(true)
  }

  function onAvatarUploadSuccess({ url }: { url: string }) {
    setAvatar(url)
    setMetadataChanged(true)
  }
})

ProfileEditorPage.displayName = 'ProfileEditorPage'
export default ProfileEditorPage

function Item({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-2">{children}</div>
}
